// Upgrade catalogue and how a player's chosen upgrades change their tuning and rules.

import type { CarTuning } from './car';
import { Rng } from './rng';

export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';
export interface Upgrade { id: string; name: string; desc: string; rarity: Rarity; max: number; icon: string; }

export const UPGRADES: Upgrade[] = [
  { id: 'turbo', name: 'Turbo Engine', desc: '+10% top speed', rarity: 'common', max: 3, icon: 'engine' },
  { id: 'tyres', name: 'Grippy Tyres', desc: '+15% steering response', rarity: 'common', max: 3, icon: 'tyre' },
  { id: 'bumper', name: 'Bumper Bars', desc: '−20% collision damage taken', rarity: 'common', max: 3, icon: 'bumper' },
  { id: 'spare', name: 'Spare Tyre', desc: '+25 max health, heal 25 now', rarity: 'common', max: 3, icon: 'spare' },
  { id: 'tips', name: 'Big Tips', desc: '+10% on all fares', rarity: 'common', max: 3, icon: 'cash' },
  { id: 'timer', name: 'Time Extender', desc: '+15% fare timer', rarity: 'common', max: 3, icon: 'clock' },
  { id: 'quickstop', name: 'Quick Stop', desc: 'Pickup and drop-off rings 30% bigger', rarity: 'common', max: 2, icon: 'ring' },
  { id: 'lucky', name: 'Lucky Coin', desc: 'Rerolls cost half', rarity: 'common', max: 1, icon: 'coin' },
  { id: 'mechanic', name: 'Mechanic Buddy', desc: 'Regenerate 1 HP per second', rarity: 'rare', max: 2, icon: 'wrench' },
  { id: 'fasttalk', name: 'Fast Talker', desc: 'Passengers hop in instantly', rarity: 'rare', max: 1, icon: 'talk' },
  { id: 'nitro', name: 'Nitro Button', desc: '2 s boost, 10 s cooldown', rarity: 'rare', max: 2, icon: 'nitro' },
  { id: 'horn', name: 'Horn of Doom', desc: 'Honking shoves traffic and pedestrians aside', rarity: 'rare', max: 1, icon: 'horn' },
  { id: 'shield', name: 'Storm Shield', desc: 'Half damage from disasters', rarity: 'rare', max: 1, icon: 'shield' },
  { id: 'smooth', name: 'Smooth Talker', desc: 'Police give up twice as fast', rarity: 'rare', max: 1, icon: 'badge' },
  { id: 'halo', name: 'Halo', desc: 'Good meter gains doubled', rarity: 'rare', max: 1, icon: 'halo' },
  { id: 'magnet', name: 'Coin Magnet', desc: 'Dropped money pulls toward you', rarity: 'rare', max: 1, icon: 'magnet' },
  { id: 'roofrack', name: 'Roof Rack', desc: 'Carry two passengers at once', rarity: 'epic', max: 1, icon: 'rack' },
  { id: 'freeze', name: 'Meter Freeze', desc: 'Pause fare timers 8 s, 30 s cooldown', rarity: 'epic', max: 1, icon: 'freeze' },
  { id: 'bouncy', name: 'Bouncy Castle', desc: 'Collisions cost no health but bounce you hard', rarity: 'epic', max: 1, icon: 'bounce' },
  { id: 'satnav', name: 'Sat Nav', desc: 'Destinations count from 15 m away', rarity: 'epic', max: 1, icon: 'satnav' },
  { id: 'golden', name: 'Golden Cab', desc: '+25% fares, but +25% damage taken', rarity: 'epic', max: 1, icon: 'gold' },
  { id: 'jet', name: 'Jet Taxi', desc: 'Tap to jump over anything, 6 s cooldown', rarity: 'legendary', max: 1, icon: 'jet' },
  { id: 'ghost', name: 'Ghost Cab', desc: 'Pass through traffic for 5 s, 25 s cooldown', rarity: 'legendary', max: 1, icon: 'ghost' },
  { id: 'rain', name: 'Money Rain', desc: 'Cash showers around your car for 10 s', rarity: 'legendary', max: 1, icon: 'rain' },
];
export const UPGRADE_BY_ID = new Map(UPGRADES.map((u) => [u.id, u]));

/** Rarity odds by rank: first, middle, last (GDD section 7). */
const ODDS: Record<'first' | 'middle' | 'last', Record<Rarity, number>> = {
  first: { common: 0.65, rare: 0.30, epic: 0.05, legendary: 0 },
  middle: { common: 0.45, rare: 0.35, epic: 0.17, legendary: 0.03 },
  last: { common: 0.20, rare: 0.35, epic: 0.30, legendary: 0.15 },
};

export function rankBucket(rank: number, players: number): 'first' | 'middle' | 'last' {
  if (players <= 1) return 'middle';
  if (rank === 0) return 'first';
  if (rank === players - 1) return 'last';
  return 'middle';
}

export function rollOffer(rng: Rng, owned: Map<string, number>, bucket: 'first' | 'middle' | 'last', count = 3): Upgrade[] {
  const odds = ODDS[bucket];
  const available = UPGRADES.filter((u) => (owned.get(u.id) ?? 0) < u.max);
  const picked: Upgrade[] = [];
  for (let i = 0; i < count && available.length; i++) {
    const r = rng.next();
    let rarity: Rarity = 'common'; let acc = 0;
    for (const k of ['common', 'rare', 'epic', 'legendary'] as Rarity[]) { acc += odds[k]; if (r <= acc) { rarity = k; break; } }
    let pool = available.filter((u) => u.rarity === rarity && !picked.includes(u));
    if (!pool.length) pool = available.filter((u) => !picked.includes(u));
    if (!pool.length) break;
    picked.push(rng.pick(pool));
  }
  return picked;
}

export const REROLL_COSTS = [30, 60, 120];

export interface Perks {
  tuning: Partial<CarTuning>;
  damageTaken: number;   // multiplier
  maxHealth: number;
  regen: number;         // hp/s
  fareBonus: number;     // fraction
  timerBonus: number;    // fraction
  ringScale: number;
  rerollHalf: boolean;
  instantPickup: boolean;
  nitro: boolean;
  hornShove: boolean;
  disasterDamage: number;
  policePatience: number;
  karmaGain: number;
  magnet: boolean;
  capacity: number;
  meterFreeze: boolean;
  bouncy: boolean;
  dropRadiusBonus: number;
  jet: boolean;
  ghost: boolean;
  moneyRain: boolean;
}

export function computePerks(owned: Map<string, number>, base: CarTuning): Perks {
  const n = (id: string) => owned.get(id) ?? 0;
  const p: Perks = {
    tuning: { maxSpeed: base.maxSpeed * (1 + 0.1 * n('turbo')), steerRate: base.steerRate * (1 + 0.15 * n('tyres')), restitution: n('bouncy') ? 0.9 : base.restitution },
    damageTaken: Math.pow(0.8, n('bumper')) * (n('golden') ? 1.25 : 1) * (n('bouncy') ? 0 : 1),
    maxHealth: 100 + 25 * n('spare'),
    regen: 1 * n('mechanic'),
    fareBonus: 0.1 * n('tips') + (n('golden') ? 0.25 : 0),
    timerBonus: 0.15 * n('timer'),
    ringScale: Math.pow(1.3, n('quickstop')),
    rerollHalf: n('lucky') > 0,
    instantPickup: n('fasttalk') > 0,
    nitro: n('nitro') > 0,
    hornShove: n('horn') > 0,
    disasterDamage: n('shield') ? 0.5 : 1,
    policePatience: n('smooth') ? 0.5 : 1,
    karmaGain: n('halo') ? 2 : 1,
    magnet: n('magnet') > 0,
    capacity: n('roofrack') ? 2 : 1,
    meterFreeze: n('freeze') > 0,
    bouncy: n('bouncy') > 0,
    dropRadiusBonus: n('satnav') ? 15 : 0,
    jet: n('jet') > 0,
    ghost: n('ghost') > 0,
    moneyRain: n('rain') > 0,
  };
  return p;
}
